import React, { ErrorInfo } from 'react'
import { Link } from 'react-router'

interface Props {
  children: React.ReactNode
}

interface State {
  hasError: boolean
}

/**
 * Catches render errors in the subtree and shows an editorial fallback
 * instead of a blank screen. Non-render errors (e.g. failed fetch) are
 * unchanged — those stay on the page that threw in event handlers.
 */
export default class AppErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('AppErrorBoundary:', error, info.componentStack)
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false })
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div className="min-h-[50vh] flex flex-col items-center justify-center px-4 py-16 text-center">
          <p className="font-mono text-xs uppercase tracking-widest text-signal mb-3">
            Noget gik galt
          </p>
          <h1 className="font-serif text-2xl sm:text-3xl text-ink mb-4 max-w-md">
            Siden kunne ikke vises korrekt
          </h1>
          <p className="text-sm text-ink-600 max-w-md mb-8">
            En uventet fejl opstod under visningen. Prøv at genindlæse
            siden, eller gå til forsiden og fortsæt derfra.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={this.handleRetry}
              className="px-4 py-2 text-sm border-2 border-ink bg-ink text-paper rounded-sm hover:bg-ink-700 transition-colors"
            >
              Prøv igen
            </button>
            <Link
              to="/"
              className="px-4 py-2 text-sm border-2 border-rule text-ink rounded-sm hover:bg-ink-50 transition-colors"
            >
              Til forsiden
            </Link>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
